import { FC, useMemo, useState, useTransition } from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from '@/components/ui/input';
import EPageLoader from '@/components/everridge/EPageLoader';
import SuperAdminDataPaginatedTable from '@/features/superAdmin/features/data/shared/components/SuperAdminDataPaginatedTable';
import { useRentChargeInfoNestedColumns } from '@/features/superAdmin/features/data/rentCharges/components/SuperAdminRentChargesPageColumns';
import { SuperAdminRentChargeInfoDto } from '@/features/superAdmin/features/data/types/superAdmin-data.types';

const AMOUNT_FIELDS: (keyof SuperAdminRentChargeInfoDto)[] = [
    'roomsRented',
    'revenue',
    'revenueVat',
    'electricity',
    'water',
    'internet',
    'comissions',
    'incomeTax',
    'total',
    'adGas',
    'adPropertyManagement',
    'adAgentCommission',
    'utilities',
    'previousMonthUtilities',
    'totalAfterPropertyManagement',
    'startingBalance',
    'paidToOwnerSum',
    'totalCurrentMonth',
    'currentBalance',
    'grossRevenue',
    'deduction',
    'transferredFunds',
];

const matchesSearch = (info: SuperAdminRentChargeInfoDto, query: string) => {
    const roomText = `${info.apartmentName ?? ''} ${info.apartmentId}`.toLowerCase();
    if (roomText.includes(query)) return true;

    return AMOUNT_FIELDS.some((field) => String(info[field] ?? '').toLowerCase().includes(query));
};

type Props = {
    infos: SuperAdminRentChargeInfoDto[];
};

const SuperAdminRentChargeInfosTab: FC<Props> = ({ infos }) => {
    const { t } = useTranslation();
    const columns = useRentChargeInfoNestedColumns();
    const [searchInput, setSearchInput] = useState('');
    const [appliedSearch, setAppliedSearch] = useState('');
    const [isPending, startTransition] = useTransition();

    const filteredInfos = useMemo(() => {
        const query = appliedSearch.trim().toLowerCase();
        if (!query) return infos;
        return infos.filter((info) => matchesSearch(info, query));
    }, [infos, appliedSearch]);

    const handleSearchChange = (value: string) => {
        setSearchInput(value);
        startTransition(() => {
            setAppliedSearch(value);
        });
    };

    return (
        <div className="flex flex-col gap-4">
            {isPending && <EPageLoader text={t('superAdmin.data.rentCharges.filtering')} />}
            <Input
                value={searchInput}
                onChange={(event) => handleSearchChange(event.target.value)}
                placeholder={t('superAdmin.data.rentCharges.infosSearchPlaceholder')}
            />
            <SuperAdminDataPaginatedTable
                key={appliedSearch}
                columns={columns}
                data={filteredInfos}
            />
        </div>
    );
};

export default SuperAdminRentChargeInfosTab;
